import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  addDays,
  defaultDependsOn,
  formatDate,
  readableInk,
  todayIn,
  type BatchView,
  type JobDetail,
  type PlanNudge,
  type StationKey,
  type StepPlan,
  type StepStatus,
} from '@jobshop/shared';
import {
  useAddBatch,
  useDeleteBatch,
  useDeleteJob,
  useFixPlanMove,
  useJob,
  useJobScans,
  usePatchBatch,
  usePatchJob,
  useReleasePlanMoves,
  useScheduleBatch,
  useScheduleJob,
  useSetJobPinned,
  useSetStep,
  useShopTimeZone,
  useStationName,
  useTemplates,
  useUnscheduleBatch,
} from '../../api/hooks';
import { useSession } from '../../auth/useUser';
import { JobNav } from './JobNav';
import { ScanHistory } from './ScanHistory';
import {
  BadgeChip,
  Card,
  CardHeader,
  ErrorNote,
  Field,
  Modal,
  Pill,
  Spinner,
  WarningChip,
} from '../../components/ui';

export function JobPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const job = useJob(jobId);
  const scans = useJobScans(jobId);
  const session = useSession();
  const scheduleJob = useScheduleJob();
  const patchJob = usePatchJob(jobId ?? '');
  const setPinned = useSetJobPinned(jobId ?? '');
  const timeZone = useShopTimeZone();
  const [splitting, setSplitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  if (job.isLoading) return <Spinner />;
  if (job.error) return <ErrorNote error={job.error} />;
  if (!job.data) return null;

  const detail: JobDetail = job.data;
  // Normally a job moves through the shop as one piece, and then batches never appear.
  const multiBatch = detail.batches.length > 1;
  // Pinning is all-or-nothing per job: a plan is either held in place or it is not.
  const planned = detail.batches.flatMap((b) => b.plan).filter((p) => p.hoursBooked > 0);
  const allPinned = planned.length > 0 && planned.every((p) => p.pinned);

  const startRename = () => {
    setNameDraft(detail.name);
    setEditingName(true);
  };

  const isArchived = !!detail.archivedAt;
  const finished = detail.status === 'done' || detail.status === 'cancelled';
  // A finished job goes straight to the archive — that is the normal case and it is
  // reversible. A job that is still draft or active gets asked first, because putting
  // it away does not free the hours it has booked.
  const archive = () => (finished ? patchJob.mutate({ archived: true }) : setArchiving(true));

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Link to="/jobs" className="text-ink-400 hover:underline">
                Jobs
              </Link>
              <span className="text-ink-300">/</span>
              {editingName ? (
                <input
                  className="field w-auto py-0.5"
                  value={nameDraft}
                  autoFocus
                  aria-label="Job name"
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={() => {
                    const next = nameDraft.trim();
                    if (next !== '' && next !== detail.name) patchJob.mutate({ name: next });
                    setEditingName(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setEditingName(false);
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
              ) : (
                // Renaming is rare and the name sits where the eye lands, so a plain
                // click does nothing: it was being triggered by accident far more often
                // than on purpose. Right-click is the shortcut for a mouse, and the
                // "Rename job" button is the path that works on a tablet too.
                <span
                  className="rounded px-1.5 py-0.5"
                  style={{ backgroundColor: detail.color, color: readableInk(detail.color) }}
                  title={session.can('planner') ? 'Right-click to rename' : undefined}
                  onContextMenu={(e) => {
                    if (!session.can('planner')) return;
                    e.preventDefault();
                    startRename();
                  }}
                >
                  {detail.name}
                </span>
              )}
              <JobNav
                jobId={detail._id}
                current="plan"
                partsBatchId={multiBatch ? undefined : detail.batches[0]?._id}
              />
              {session.can('planner') ? (
                <input
                  type="color"
                  className="h-6 w-6 shrink-0 cursor-pointer rounded border border-ink-200 p-0"
                  value={detail.color}
                  onChange={(e) => patchJob.mutate({ color: e.target.value })}
                  title="Job colour — followable across the board and jobs list"
                  aria-label="Job colour"
                />
              ) : null}
              <Pill tone={detail.status === 'active' ? 'info' : 'neutral'}>{detail.status}</Pill>
              {isArchived ? <Pill title="This job is in the archive.">archived</Pill> : null}
            </span>
          }
          subtitle={
            <span className="flex flex-wrap items-center gap-2">
              {detail.customer ? <span>{detail.customer}</span> : null}
              {/* Planners read it off the editable control in the actions row instead. */}
              {session.can('planner') ? null : <span>priority {detail.priority}</span>}
              {detail.badges.length > 0 ? (
                <span className="flex flex-wrap gap-1">
                  {[...new Set(detail.badges.map((b) => b.code))].map((code) => (
                    <BadgeChip key={code} badge={detail.badges.find((b) => b.code === code)!} />
                  ))}
                </span>
              ) : null}
            </span>
          }
          actions={
            session.can('planner') ? (
              <>
                <select
                  className="field w-auto"
                  value={detail.status}
                  onChange={(e) =>
                    patchJob.mutate({ status: e.target.value as JobDetail['status'] })
                  }
                  aria-label="Job status"
                >
                  <option value="draft">draft</option>
                  <option value="active">active</option>
                  <option value="done">done</option>
                  <option value="cancelled">cancelled</option>
                </select>
                <label
                  className="flex items-center gap-1 text-xs uppercase tracking-wide text-ink-500"
                  title="Higher goes first when jobs compete for the same capacity. Takes effect on the next re-plan."
                >
                  Priority
                  <input
                    type="number"
                    step={100}
                    // Uncommitted keystrokes shouldn't each fire a PATCH, so this commits
                    // on blur — the same way the station machine caps do in Admin.
                    key={detail.priority}
                    defaultValue={detail.priority}
                    className="field w-24 tabular-nums"
                    aria-label="Job priority"
                    onBlur={(e) => {
                      const next = Number(e.target.value);
                      if (e.target.value.trim() === '' || !Number.isInteger(next)) {
                        e.target.value = String(detail.priority); // nothing usable typed
                        return;
                      }
                      if (next !== detail.priority) patchJob.mutate({ priority: next });
                    }}
                  />
                </label>
                <button className="btn-secondary" onClick={() => setSplitting(true)}>
                  Add batch
                </button>
                <button
                  className="btn-primary"
                  disabled={scheduleJob.isPending}
                  onClick={() => scheduleJob.mutate(detail._id)}
                  title={
                    multiBatch
                      ? 'Re-plans every batch of this job, in order, into the same capacity map. Other jobs stay where they are — only "Re-plan all jobs" on the board can move those.'
                      : 'Re-plans this job. Other jobs stay where they are — only "Re-plan all jobs" on the board can move those.'
                  }
                >
                  {scheduleJob.isPending
                    ? 'Re-planning…'
                    : multiBatch
                      ? 'Re-plan whole job'
                      : 'Re-plan job'}
                </button>
                {planned.length > 0 ? (
                  <button
                    className="btn-secondary"
                    disabled={setPinned.isPending}
                    onClick={() => setPinned.mutate(!allPinned)}
                    title={
                      allPinned
                        ? 'Release this plan so a re-plan may move it again.'
                        : 'Hold every booking of this job where it is. A re-plan then works around it instead of moving it.'
                    }
                  >
                    {setPinned.isPending ? 'Saving…' : allPinned ? '📌 Unpin plan' : 'Pin plan'}
                  </button>
                ) : null}
                <button
                  className="btn-secondary"
                  onClick={startRename}
                  title="Renaming a job is rare, so it lives here rather than on the name itself. Right-clicking the name does the same."
                >
                  Rename job
                </button>
                {isArchived ? (
                  <button
                    className="btn-secondary"
                    disabled={patchJob.isPending}
                    onClick={() => patchJob.mutate({ archived: false })}
                    title="Bring this job back to the current Jobs list. Its status, plan and bookings are untouched."
                  >
                    Restore job
                  </button>
                ) : (
                  <button
                    className="btn-secondary"
                    disabled={patchJob.isPending}
                    onClick={archive}
                    title="Put this job away: it leaves the current Jobs list and Re-plan all, but stays readable under Archived and can be restored at any time. Nothing is deleted."
                  >
                    Archive job
                  </button>
                )}
                <button className="btn-danger" onClick={() => setDeleting(true)}>
                  Delete job
                </button>
              </>
            ) : null
          }
        />
        {isArchived ? (
          <p className="border-b border-ink-100 bg-ink-50 px-4 py-2 text-xs text-ink-600">
            Archived{detail.archivedAt ? ` on ${formatDate(todayIn(timeZone, new Date(detail.archivedAt)))}` : ''}
            {detail.archivedBy ? ` by ${detail.archivedBy}` : ''}. Everything here still
            works, but the job is left out of the current Jobs list and of{' '}
            <em>Re-plan all jobs</em>. Find it under <Link to="/jobs" className="underline">Jobs › Archived</Link>
            {session.can('planner') ? ', or restore it with the button above' : ''}.
          </p>
        ) : null}
        <div className="px-4 py-3">
          <ErrorNote error={scheduleJob.error ?? patchJob.error ?? setPinned.error} />
        </div>
      </Card>

      {detail.batches.map((batch) => (
        <BatchCard
          key={batch._id}
          jobId={detail._id}
          batch={batch}
          showBatchName={multiBatch}
          canDelete={multiBatch}
          scanCount={scans.data?.scans.filter((s) => s.batchId === batch._id).length ?? 0}
        />
      ))}

      <ScanHistory jobId={detail._id} />

      <AddBatchModal jobId={detail._id} open={splitting} onClose={() => setSplitting(false)} />
      <ArchiveJobModal
        job={detail}
        open={archiving}
        onClose={() => setArchiving(false)}
        onConfirm={() => patchJob.mutate({ archived: true }, { onSuccess: () => setArchiving(false) })}
        pending={patchJob.isPending}
        error={patchJob.error}
      />
      <DeleteJobModal
        job={detail}
        scanCount={scans.data?.scans.length ?? 0}
        open={deleting}
        onClose={() => setDeleting(false)}
      />
    </div>
  );
}

/**
 * Only an unfinished job asks before going to the archive. Archiving is a filing
 * gesture, not a planning one: the job's bookings stay on the board and keep their
 * capacity, exactly as a done job's do — so a still-active job is probably one that
 * should be marked done or cancelled first.
 */
function ArchiveJobModal({
  job,
  open,
  onClose,
  onConfirm,
  pending,
  error,
}: {
  job: JobDetail;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pending: boolean;
  error: unknown;
}) {
  const booked = job.batches.flatMap((b) => b.plan).reduce((s, p) => s + p.hoursBooked, 0);
  return (
    <Modal open={open} onClose={onClose} title="Archive an unfinished job?">
      <div className="space-y-2 text-sm text-ink-700">
        <p>
          <strong>{job.name}</strong> is still <strong>{job.status}</strong>. Archiving puts
          it away — off the current Jobs list and out of <em>Re-plan all jobs</em> — but
          changes nothing else: its status stays {job.status}
          {booked > 0 ? `, and its ${booked}h of booked station time stay on the board` : ''}.
        </p>
        <p>
          If the work is finished, mark the job <em>done</em> first so the archive is honest
          about it. If it is simply on hold, archiving is a fine way to park it: restoring
          brings it back exactly as it is.
        </p>
      </div>
      <ErrorNote error={error} />
      <div className="flex justify-end gap-2 pt-3">
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn-primary" disabled={pending} onClick={onConfirm}>
          {pending ? 'Archiving…' : 'Archive anyway'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Deleting a job takes its batches, its plan, its parts and the scans recorded against
 * them — the most destructive thing the app can do, and there is no undo. The job's
 * name has to be typed back whenever there is real work to lose; for an empty draft
 * that would be ceremony, so it isn't asked for.
 */
function DeleteJobModal({
  job,
  scanCount,
  open,
  onClose,
}: {
  job: JobDetail;
  scanCount: number;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const remove = useDeleteJob();
  const [typed, setTyped] = useState('');

  const partsTotal = job.batches.reduce((sum, b) => sum + b.partsTotal, 0);
  const partsTracked = job.batches.reduce((sum, b) => sum + b.partsTracked, 0);
  const planned = job.batches.some((b) => b.finishDate);

  const mustType = partsTotal > 0 || scanCount > 0;
  const confirmed = !mustType || typed.trim() === job.name;

  const close = () => {
    setTyped('');
    onClose();
  };

  return (
    <Modal open={open} onClose={close} title={`Delete ${job.name}?`}>
      <div className="space-y-3">
        <p className="text-sm text-ink-700">This cannot be undone. It also deletes:</p>
        <ul className="list-inside list-disc space-y-1 text-sm text-ink-700">
          <li>
            {job.batches.length} batch{job.batches.length === 1 ? '' : 'es'}
            {planned ? ', and the plan — every booking this job holds at every station' : ''}
          </li>
          <li>
            {partsTotal} part{partsTotal === 1 ? '' : 's'}
            {partsTracked > 0 ? ` (${partsTracked} labelled)` : ''}
          </li>
          <li>
            {scanCount} scan{scanCount === 1 ? '' : 's'} recorded against it
          </li>
          {job.notes.length > 0 ? (
            <li>
              {job.notes.length} note{job.notes.length === 1 ? '' : 's'}
            </li>
          ) : null}
        </ul>

        {partsTracked > 0 ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {partsTracked} printed label{partsTracked === 1 ? '' : 's'} for this job will stop
            resolving — scanning one afterwards finds nothing. Labels already stuck to cabinets
            cannot be un-printed.
          </p>
        ) : null}

        {mustType ? (
          <Field label={`Type "${job.name}" to confirm`}>
            <input
              className="field"
              value={typed}
              placeholder={job.name}
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
              aria-label="Confirm the job name"
            />
          </Field>
        ) : null}

        <ErrorNote error={remove.error} />

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={!confirmed || remove.isPending}
            onClick={() =>
              remove.mutate(job._id, { onSuccess: () => navigate('/jobs', { replace: true }) })
            }
          >
            {remove.isPending ? 'Deleting…' : 'Delete job'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function BatchCard({
  jobId,
  batch,
  showBatchName,
  canDelete,
  scanCount,
}: {
  jobId: string;
  batch: BatchView;
  showBatchName: boolean;
  canDelete: boolean;
  scanCount: number;
}) {
  const session = useSession();
  const schedule = useScheduleBatch();
  const unschedule = useUnscheduleBatch();
  const removeBatch = useDeleteBatch();
  const releaseMoves = useReleasePlanMoves();
  const handMoved = batch.routing.filter((s) => s.nudge);
  const [editing, setEditing] = useState(false);
  const today = todayIn(useShopTimeZone());

  const planByStation = new Map(batch.plan.map((p) => [p.stationKey, p]));

  return (
    <Card>
      <CardHeader
        title={showBatchName ? batch.name : 'Plan'}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span>
              {formatDate(batch.dateStart)} → delivery {formatDate(batch.dateDelivery)}
            </span>
            {batch.finishDate ? (
              <span
                className={clsx(
                  batch.finishDate > batch.dateDelivery
                    ? 'font-medium text-red-700'
                    : 'text-ink-500',
                )}
              >
                planned finish {formatDate(batch.finishDate)}
              </span>
            ) : (
              <span className="text-ink-400">not scheduled</span>
            )}
            <span title="Labelling is opt-in — most parts carry no QR label.">
              {batch.partsTracked} of {batch.partsTotal} parts tracked
            </span>
            {batch.warnings.map((w, i) => (
              <WarningChip key={i} warning={w} />
            ))}
            {batch.badges.map((b, i) => (
              <BadgeChip key={i} badge={b} />
            ))}
          </span>
        }
        actions={
          <>
            {/* Only when there are several batches: a single-batch job reaches its parts
                from the header nav, which does not have to name a batch to do it. */}
            {showBatchName ? (
              <Link className="btn-secondary" to={`/jobs/${jobId}/batches/${batch._id}/parts`}>
                Parts
              </Link>
            ) : null}
            {session.can('planner') ? (
              <>
                <button className="btn-secondary" onClick={() => setEditing(true)}>
                  Edit hours
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => unschedule.mutate(batch._id)}
                  disabled={unschedule.isPending}
                >
                  Clear plan
                </button>
                <button
                  className="btn-primary"
                  onClick={() => schedule.mutate(batch._id)}
                  disabled={schedule.isPending}
                  title={
                    showBatchName
                      ? "Re-plans only this batch. Every other booking — this job's other batches included — is treated as fixed load."
                      : 'Re-plans this job. The same thing the button in the header does, since this job has a single batch.'
                  }
                >
                  {schedule.isPending
                    ? 'Re-planning…'
                    : showBatchName
                      ? 'Re-plan this batch'
                      : 'Re-plan job'}
                </button>
                {handMoved.length > 0 ? (
                  <button
                    className="btn-secondary"
                    disabled={releaseMoves.isPending}
                    onClick={() => releaseMoves.mutate(batch._id)}
                    title="Drops every move made by dragging on the board and re-plans. A date fixed on purpose is kept."
                  >
                    {releaseMoves.isPending
                      ? 'Clearing…'
                      : `Clear ${handMoved.length} hand-move${handMoved.length === 1 ? '' : 's'}`}
                  </button>
                ) : null}
                {canDelete ? (
                  <button
                    className="btn-danger"
                    onClick={() => {
                      if (confirm(`Delete batch "${batch.name}" and its parts and plan?`)) {
                        removeBatch.mutate(batch._id);
                      }
                    }}
                  >
                    Delete
                  </button>
                ) : null}
              </>
            ) : null}
          </>
        }
      />

      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
            <tr>
              <th className="px-4 py-2 font-medium">Station</th>
              <th className="px-4 py-2 font-medium">Hours</th>
              <th className="px-4 py-2 font-medium">Planned</th>
              <th className="px-4 py-2 font-medium">Progress</th>
              <th className="px-4 py-2 font-medium">Scan evidence</th>
            </tr>
          </thead>
          <tbody>
            {batch.routing.map((step, i) => (
              <StepRow
                key={step.stationKey}
                batchId={batch._id}
                stationKey={step.stationKey}
                dependsOn={step.dependsOn ?? defaultDependsOn(batch.routing, i)}
                hoursWork={step.hoursWork}
                nudge={step.nudge}
                notBeforeDate={step.notBeforeDate}
                status={step.actual.status}
                by={step.actual.by}
                completedAt={step.actual.completedAt}
                plan={planByStation.get(step.stationKey)}
                partsTracked={batch.partsTracked}
                today={today}
              />
            ))}
          </tbody>
        </table>
      </div>

      <p className="border-t border-ink-200 px-4 py-2 text-xs text-ink-500">
        Ticking a station off is the authoritative record of what is done. Scans corroborate it and
        never change it — {scanCount} scan{scanCount === 1 ? '' : 's'} recorded against this batch.
      </p>

      <EditRoutingModal batch={batch} open={editing} onClose={() => setEditing(false)} />
    </Card>
  );
}

function StepRow({
  batchId,
  stationKey,
  dependsOn,
  hoursWork,
  nudge,
  notBeforeDate,
  status,
  by,
  completedAt,
  plan,
  partsTracked,
  today,
}: {
  batchId: string;
  stationKey: StationKey;
  dependsOn: StationKey[];
  hoursWork: number;
  nudge?: PlanNudge;
  notBeforeDate?: string;
  status: StepStatus;
  by?: string;
  completedAt?: Date | string;
  plan: StepPlan | undefined;
  partsTracked: number;
  today: string;
}) {
  const session = useSession();
  const setStep = useSetStep(batchId);
  const fixMove = useFixPlanMove(batchId);
  const stationName = useStationName();
  const late = plan?.finish && plan.finish < today && status !== 'done';

  return (
    <tr className="border-b border-ink-100 last:border-0">
      <td className="px-4 py-2 font-medium">
        {stationName(stationKey)}
        {dependsOn.length > 0 ? (
          <span
            className="ml-1 font-normal text-ink-400"
            title="Starts after this station finishes"
          >
            ← {dependsOn.map((k) => stationName(k)).join(', ')}
          </span>
        ) : null}
      </td>
      <td className="px-4 py-2 text-ink-600">
        {hoursWork}h
        {plan && plan.hoursBooked > 0 && plan.hoursBooked !== hoursWork ? (
          <span className="ml-1 text-xs text-ink-400">({plan.hoursBooked}h booked)</span>
        ) : null}
      </td>
      <td className={clsx('px-4 py-2', late ? 'text-red-700' : 'text-ink-600')}>
        {plan?.start && plan.finish ? (
          <>
            {formatDate(plan.start)}
            {plan.start !== plan.finish ? ` → ${formatDate(plan.finish)}` : ''}
            {plan.pinned ? <span title="Contains a pinned booking"> 📌</span> : null}
          </>
        ) : (
          <span className="text-ink-400">—</span>
        )}
        {/*
          Two different things, deliberately distinguishable: a date somebody fixed on
          purpose, and a step somebody dragged. Only the second is swept away by
          "Clear hand-moves" — which is why they are separate fields.
        */}
        {notBeforeDate ? (
          <div className="text-xs text-ink-500" title="Cannot start before this date">
            not before {formatDate(notBeforeDate)}
          </div>
        ) : null}
        {nudge ? (
          <div className="flex items-center gap-1 text-xs text-ink-500">
            <span title={nudge.by ? `Dragged on the board by ${nudge.by}` : 'Dragged on the board'}>
              moved by hand → {formatDate(nudge.date)}
            </span>
            {session.can('planner') ? (
              <button
                className="underline"
                disabled={fixMove.isPending}
                onClick={() => fixMove.mutate(stationKey)}
                title="Keep this date as a real constraint — an agreed delivery date, say — so clearing hand-moves does not drop it."
              >
                fix
              </button>
            ) : null}
          </div>
        ) : null}
      </td>
      <td className="px-4 py-2">
        {session.can('planner') ? (
          <select
            className="field w-auto py-1"
            value={status}
            onChange={(e) => setStep.mutate({ stationKey, status: e.target.value as StepStatus })}
            aria-label={`${stationKey} progress`}
          >
            <option value="pending">pending</option>
            <option value="inProgress">in progress</option>
            <option value="done">done</option>
          </select>
        ) : (
          <Pill tone={status === 'done' ? 'good' : status === 'inProgress' ? 'info' : 'neutral'}>
            {status}
          </Pill>
        )}
        {status === 'done' && completedAt ? (
          <span className="ml-2 text-xs text-ink-500" title={by ? `by ${by}` : undefined}>
            {new Date(completedAt).toLocaleDateString()}
          </span>
        ) : null}
      </td>
      <td className="px-4 py-2 text-xs text-ink-600">
        {/*
          Positive-only. When nothing has been scanned we say exactly that, rather than
          implying the station is behind: with sparse labelling, "no scans" and "badly
          behind" produce identical data.
        */}
        {plan && plan.partsConfirmed > 0 ? (
          <span className={plan.slippedAt ? 'font-medium text-red-700' : undefined}>
            {plan.partsConfirmed} of {partsTracked} tracked part
            {partsTracked === 1 ? '' : 's'} confirmed here or later
            {plan.slippedAt
              ? ` · scanned ${new Date(plan.slippedAt).toLocaleDateString()}, after the planned finish`
              : ''}
          </span>
        ) : (
          <span className="text-ink-400">no scans — which proves nothing either way</span>
        )}
      </td>
    </tr>
  );
}

/** `dependsOn: undefined` shows and submits the same default the engine would apply
 * if the field were left out entirely: the row above it (see `defaultDependsOn`). */
function effectiveDependsOn(
  rows: { stationKey: StationKey; dependsOn?: StationKey[] }[],
  index: number,
): StationKey[] {
  return rows[index]!.dependsOn ?? defaultDependsOn(rows, index);
}

function EditRoutingModal({
  batch,
  open,
  onClose,
}: {
  batch: BatchView;
  open: boolean;
  onClose: () => void;
}) {
  const patch = usePatchBatch();
  const stationName = useStationName();
  const [rows, setRows] = useState(() =>
    batch.routing.map((s) => ({
      stationKey: s.stationKey,
      hoursWork: s.hoursWork,
      bufferDays: s.bufferDays,
      maxHoursPerDay: s.maxHoursPerDay,
      ...(s.dependsOn ? { dependsOn: s.dependsOn } : {}),
    })),
  );
  const [dates, setDates] = useState({
    dateStart: batch.dateStart,
    dateDelivery: batch.dateDelivery,
  });

  return (
    <Modal open={open} onClose={onClose} title={`Hours — ${batch.name}`} wide>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          patch.mutate(
            { id: batch._id, input: { ...dates, routing: rows } },
            { onSuccess: onClose },
          );
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start">
            <input
              type="date"
              className="field"
              value={dates.dateStart}
              onChange={(e) => setDates({ ...dates, dateStart: e.target.value })}
            />
          </Field>
          <Field label="Delivery">
            <input
              type="date"
              className="field"
              value={dates.dateDelivery}
              onChange={(e) => setDates({ ...dates, dateDelivery: e.target.value })}
            />
          </Field>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="py-1 font-medium">Station</th>
                <th className="py-1 font-medium">Hours</th>
                <th
                  className="py-1 font-medium"
                  title="Most this job may take at that station in one day — finishing waits on drying, and installation is one installer's day per installer on site (8h for one, 16h for two)."
                >
                  Max h/day
                </th>
                <th className="py-1 font-medium">Buffer days</th>
                <th
                  className="py-1 font-medium"
                  title="What must finish before this station may start. Defaults to the station above it; select none for a station that may start as soon as the batch does, or several to join more than one branch."
                >
                  Depends on
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.stationKey}>
                  <td className="py-1 pr-2">{stationName(row.stationKey)}</td>
                  <td className="py-1 pr-2">
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      className="field w-24 py-1"
                      value={row.hoursWork}
                      onChange={(e) =>
                        setRows(
                          rows.map((r, j) =>
                            i === j ? { ...r, hoursWork: Number(e.target.value) } : r,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      type="number"
                      min={0.5}
                      step={0.5}
                      className="field w-24 py-1"
                      value={row.maxHoursPerDay}
                      aria-label={`${stationName(row.stationKey)} max hours per day`}
                      onChange={(e) =>
                        setRows(
                          rows.map((r, j) =>
                            i === j ? { ...r, maxHoursPerDay: Number(e.target.value) } : r,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="py-1">
                    <input
                      type="number"
                      min={0}
                      className="field w-20 py-1"
                      value={row.bufferDays}
                      onChange={(e) =>
                        setRows(
                          rows.map((r, j) =>
                            i === j ? { ...r, bufferDays: Number(e.target.value) } : r,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="py-1">
                    <select
                      multiple
                      size={Math.min(3, Math.max(1, rows.length - 1))}
                      className="field w-40 py-1 text-xs"
                      aria-label={`${stationName(row.stationKey)} depends on`}
                      value={effectiveDependsOn(rows, i)}
                      onChange={(e) =>
                        setRows(
                          rows.map((r, j) =>
                            i === j
                              ? {
                                  ...r,
                                  dependsOn: Array.from(
                                    e.target.selectedOptions,
                                    (o) => o.value as StationKey,
                                  ),
                                }
                              : r,
                          ),
                        )
                      }
                    >
                      {rows
                        .filter((r) => r.stationKey !== row.stationKey)
                        .map((r) => (
                          <option key={r.stationKey} value={r.stationKey}>
                            {stationName(r.stationKey)}
                          </option>
                        ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-ink-500">
          Saving changes the estimate only. Recorded progress is kept, and the plan does not move
          until you reschedule. <strong>Max h/day</strong> is how much of this job the station can
          take in a day: lower it where the work waits on drying, and set installation to one
          installer's day for each installer on site — 8 for one, 16 for two.{' '}
          <strong>Depends on</strong> defaults to the station above it — Ctrl/Cmd-click to select
          several, or clear it for a station that should start as soon as the batch does. This
          overrides the template just for this job.
        </p>
        <ErrorNote error={patch.error} />

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={patch.isPending}>
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AddBatchModal({
  jobId,
  open,
  onClose,
}: {
  jobId: string;
  open: boolean;
  onClose: () => void;
}) {
  const templates = useTemplates();
  const add = useAddBatch(jobId);
  const today = todayIn(useShopTimeZone());
  const [form, setForm] = useState({
    name: '',
    templateName: '',
    dateStart: today,
    dateDelivery: addDays(today, 60),
  });
  const templateName = form.templateName || templates.data?.[0]?.name || '';

  return (
    <Modal open={open} onClose={onClose} title="Add a batch">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate(
            {
              name: form.name.trim(),
              templateName,
              dateStart: form.dateStart,
              dateDelivery: form.dateDelivery,
            },
            { onSuccess: onClose },
          );
        }}
      >
        <p className="text-xs text-ink-500">
          Batches let one job flow through the shop in separately deliverable pieces — "Second
          floor", "2nd kitchen". Part codes stay unique across the whole job, so printed labels are
          unaffected.
        </p>
        <Field label="Batch name">
          <input
            className="field"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Template">
          <select
            className="field"
            value={templateName}
            onChange={(e) => setForm({ ...form, templateName: e.target.value })}
          >
            {(templates.data ?? []).map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start">
            <input
              type="date"
              className="field"
              value={form.dateStart}
              onChange={(e) => setForm({ ...form, dateStart: e.target.value })}
            />
          </Field>
          <Field label="Delivery">
            <input
              type="date"
              className="field"
              value={form.dateDelivery}
              onChange={(e) => setForm({ ...form, dateDelivery: e.target.value })}
            />
          </Field>
        </div>

        <ErrorNote error={add.error} />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={add.isPending}>
            Add batch
          </button>
        </div>
      </form>
    </Modal>
  );
}
